import React from 'react'
import styled from '@emotion/styled'
import { Button, Card, Modal, Space } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import {
  PortalArticleBlock,
  PortalArticleTopicBlock,
  PortalEditorSectionTarget,
} from '../../../models/PortalArtigoModel'
import {
  createDefaultBlock,
  createDefaultTopic,
  createPortalEditorBlockId,
  cloneSectionContentWithFreshTableIds,
} from '../helpers/block-defaults'
import { syncBlockAnchorChanges } from '../helpers/block-anchor-sync'
import { moveSubtopicBetweenTopics, syncSummarySubtopicOrder } from '../helpers/topic-utils'
import { removeSummaryReferencesForAnchors } from '../helpers/anchor-utils'
import { PortalTopicEditor } from './blocks/PortalTopicEditor'
import { SectionEditor } from './blocks/SectionEditor'
import { SummaryTopicsEditor } from './blocks/SummaryTopicsEditor'
import { PortalSectionSaveControlProps } from '../helpers/editor-section-save'
import { PortalSectionSaveButton } from './PortalEditorTheme'

interface Props {
  value: PortalArticleBlock[]
  onChange: (blocks: PortalArticleBlock[]) => void
  editingBlockIndexes?: number[]
  onEditingBlockIndexesChange: (blockIndexes: number[]) => void
  onBlockCreated?: (blockIndex: number) => void
  isPillar?: boolean
  isPublished?: boolean
  getSectionSaveControlProps: (target: PortalEditorSectionTarget) => PortalSectionSaveControlProps
}

export function PortalArticleBlockEditor({
  value,
  onChange,
  editingBlockIndexes = [],
  onEditingBlockIndexesChange,
  onBlockCreated,
  isPublished = false,
  getSectionSaveControlProps,
}: Props): React.ReactElement {
  const blocks = value || []
  const topics = blocks.filter((block): block is PortalArticleTopicBlock => block.type === 'topic')
  const hasIntro = blocks.some((block) => block.type === 'intro')
  const hasSummary = blocks.some((block) => block.type === 'summary')
  const editorRows = createEditorRows(blocks, hasIntro, hasSummary)

  const setExpanded = (blockIndex: number, expanded: boolean) => {
    onEditingBlockIndexesChange(
      expanded
        ? Array.from(new Set([...editingBlockIndexes, blockIndex])).sort((a, b) => a - b)
        : editingBlockIndexes.filter((index) => index !== blockIndex)
    )
  }

  const keepInsertedBlockExpanded = (insertedIndex: number) => {
    onEditingBlockIndexesChange(
      Array.from(
        new Set([
          ...editingBlockIndexes.map((index) => (index >= insertedIndex ? index + 1 : index)),
          insertedIndex,
        ])
      ).sort((a, b) => a - b)
    )
  }

  const removeEditingBlockIndex = (removedIndex: number) => {
    onEditingBlockIndexesChange(
      editingBlockIndexes
        .filter((index) => index !== removedIndex)
        .map((index) => (index > removedIndex ? index - 1 : index))
    )
  }

  const swapEditingBlockIndexes = (firstIndex: number, secondIndex: number) => {
    onEditingBlockIndexesChange(
      editingBlockIndexes
        .map((index) => {
          if (index === firstIndex) return secondIndex
          if (index === secondIndex) return firstIndex
          return index
        })
        .sort((a, b) => a - b)
    )
  }

  const updateBlock = (blockIndex: number, block: PortalArticleBlock) => {
    const previous = blocks[blockIndex]
    const next = blocks.map((current, index) => (index === blockIndex ? block : current))
    const synced = syncBlockAnchorChanges(next, previous, block)
    onChange(block.type === 'topic' ? syncSummarySubtopicOrder(synced, block) : synced)
  }

  const addIntro = () => {
    if (blocks.some((block) => block.type === 'intro')) return
    const next = [createDefaultBlock('intro'), ...blocks]
    onChange(next)
    keepInsertedBlockExpanded(0)
    onBlockCreated?.(0)
  }

  const addSummary = () => {
    if (blocks.some((block) => block.type === 'summary')) return
    const introIndex = blocks.findIndex((block) => block.type === 'intro')
    const insertedIndex = introIndex >= 0 ? introIndex + 1 : 0
    const next = [...blocks]
    next.splice(insertedIndex, 0, createDefaultBlock('summary'))
    onChange(next)
    keepInsertedBlockExpanded(insertedIndex)
    onBlockCreated?.(insertedIndex)
  }

  const addTopic = () => {
    const blockIndex = blocks.length
    onChange([...blocks, createDefaultTopic()])
    setExpanded(blockIndex, true)
    onBlockCreated?.(blockIndex)
  }

  const moveTopic = (blockIndex: number, direction: -1 | 1) => {
    const topicPositions = blocks.flatMap((block, index) => (block.type === 'topic' ? [index] : []))
    const position = topicPositions.indexOf(blockIndex)
    const target = topicPositions[position + direction]
    if (target == null) return
    const next = [...blocks]
    ;[next[blockIndex], next[target]] = [next[target], next[blockIndex]]
    onChange(next)
    swapEditingBlockIndexes(blockIndex, target)
  }

  const duplicateTopic = (blockIndex: number, topic: PortalArticleTopicBlock) => {
    const clone: PortalArticleTopicBlock = JSON.parse(JSON.stringify(topic))
    clone.id = createPortalEditorBlockId('topic')
    clone.items = clone.items.map((item) => {
      const next = { ...item, id: createPortalEditorBlockId(item.type) }
      return next.type === 'section'
        ? {
            ...next,
            section: {
              ...next.section,
              id: createPortalEditorBlockId('section'),
              anchorId: undefined,
              content: cloneSectionContentWithFreshTableIds(next.section.content),
            },
          }
        : next
    })
    const next = [...blocks]
    next.splice(blockIndex + 1, 0, clone)
    onChange(next)
    keepInsertedBlockExpanded(blockIndex + 1)
  }

  const removeRootBlock = (blockIndex: number, label: string) => {
    Modal.confirm({
      title: `Remover ${label}`,
      content: `Tem certeza que deseja remover ${label.toLowerCase()}?`,
      okText: 'Remover',
      cancelText: 'Cancelar',
      okType: 'danger',
      onOk: () => {
        const removedBlock = blocks[blockIndex]
        const next = blocks.filter((_, index) => index !== blockIndex)
        onChange(
          removedBlock?.type === 'intro'
            ? removeSummaryReferencesForAnchors(next, [
                removedBlock.section.anchorId,
                removedBlock.section.id,
              ])
            : next
        )
        removeEditingBlockIndex(blockIndex)
      },
    })
  }

  return (
    <EditorLayout>
      {editorRows.map((row) => {
        if (row.kind === 'missing') {
          const isIntro = row.blockType === 'intro'
          return (
            <MissingRootGroup
              key={`missing-${row.blockType}`}
              title={isIntro ? 'Introdução' : 'Índice'}
              addLabel={isIntro ? 'Adicionar introdução' : 'Adicionar índice'}
              required={!isIntro}
              onAdd={isIntro ? addIntro : addSummary}
            />
          )
        }

        const { block, blockIndex } = row
        const expanded = editingBlockIndexes.includes(blockIndex)
        if (block.type === 'topic') {
          const topicNumber = blocks
            .slice(0, blockIndex + 1)
            .filter((item) => item.type === 'topic').length
          return (
            <PortalTopicEditor
              key={block.id}
              topic={block}
              topicNumber={topicNumber}
              topics={topics}
              expanded={expanded}
              canMoveUp={topicNumber > 1}
              canMoveDown={topicNumber < topics.length}
              onExpandedChange={(next) => setExpanded(blockIndex, next)}
              onChange={(topic) => updateBlock(blockIndex, topic)}
              onMove={(direction) => moveTopic(blockIndex, direction)}
              onDuplicate={() => duplicateTopic(blockIndex, block)}
              onRemove={() => {
                onChange(blocks.filter((_, index) => index !== blockIndex))
                removeEditingBlockIndex(blockIndex)
              }}
              onMoveSubtopic={(itemId, targetTopicId) =>
                onChange(moveSubtopicBetweenTopics(blocks, block.id, itemId, targetTopicId))
              }
              getSectionSaveControlProps={getSectionSaveControlProps}
            />
          )
        }

        const label = block.type === 'intro' ? 'Introdução' : 'Índice'
        return (
          <RootGroup id={getPortalEditorBlockId(blockIndex)} key={block.type}>
            <GroupHeader>
              <h3>{label}</h3>
              <Space>
                <Button size="small" onClick={() => setExpanded(blockIndex, !expanded)}>
                  {expanded ? 'Recolher' : 'Expandir'}
                </Button>
                {(block.type === 'intro' || !isPublished) && (
                  <Button size="small" danger onClick={() => removeRootBlock(blockIndex, label)}>
                    Remover
                  </Button>
                )}
              </Space>
            </GroupHeader>
            {expanded ? (
              <Card size="small">
                {block.type === 'intro' ? (
                  <SectionEditor
                    value={block.section}
                    isIntro
                    onChange={(section) => updateBlock(blockIndex, { ...block, section })}
                  />
                ) : (
                  <SummaryTopicsEditor
                    blocks={blocks}
                    title={block.title}
                    topics={block.topics}
                    onChange={(patch) => updateBlock(blockIndex, { ...block, ...patch })}
                  />
                )}
                <PortalSectionSaveButton
                  {...getSectionSaveControlProps({
                    scope: 'rootBlock',
                    blockType: block.type,
                  })}
                />
              </Card>
            ) : null}
          </RootGroup>
        )
      })}
      <AddRootActions>
        <Button type="primary" icon={<PlusOutlined />} onClick={addTopic}>
          Adicionar tópico
        </Button>
      </AddRootActions>
    </EditorLayout>
  )
}

function createEditorRows(
  blocks: PortalArticleBlock[],
  hasIntro: boolean,
  hasSummary: boolean
) {
  const rows: EditorRow[] = blocks.map((block, blockIndex) => ({
    kind: 'block',
    block,
    blockIndex,
  }))

  if (!hasIntro) rows.unshift({ kind: 'missing', blockType: 'intro' })
  if (!hasSummary) {
    const introRowIndex = rows.findIndex(
      (row) =>
        (row.kind === 'missing' && row.blockType === 'intro') ||
        (row.kind === 'block' && row.block.type === 'intro')
    )
    rows.splice(introRowIndex + 1, 0, { kind: 'missing', blockType: 'summary' })
  }

  return rows
}

type EditorRow =
  | {
      kind: 'block'
      block: PortalArticleBlock
      blockIndex: number
    }
  | {
      kind: 'missing'
      blockType: 'intro' | 'summary'
    }

function MissingRootGroup({
  title,
  addLabel,
  required,
  onAdd,
}: {
  title: string
  addLabel: string
  required: boolean
  onAdd: () => void
}) {
  return (
    <RootGroup>
      <GroupHeader>
        <HeaderTitle>
          <h3>{title}</h3>
          {required && <RequiredHint>Obrigatório para publicar</RequiredHint>}
        </HeaderTitle>
        <Button size="small" icon={<PlusOutlined />} onClick={onAdd}>
          {addLabel}
        </Button>
      </GroupHeader>
    </RootGroup>
  )
}

export function getPortalEditorBlockId(blockIndex: number): string {
  return `portal-editor-block-${blockIndex}`
}

const EditorLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`
const AddRootActions = styled.div`
  display: flex;
  width: 100%;

  .ant-btn {
    width: 100%;
  }
`

const RootGroup = styled.section`
  padding: 32px;
  border-radius: 20px;
  background: #f4f4f4;
  color: #122a41;

  > .ant-card {
    border-color: #e1e4ed;
    border-radius: 8px;
  }
`
const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;

  h3 {
    margin: 0;
    color: #0f172a;
    font-size: 18px;
    font-weight: 600;
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const HeaderTitle = styled.div`
  min-width: 0;
`

const RequiredHint = styled.span`
  display: block;
  margin-top: 4px;
  color: #6b7a8c;
  font-size: 12px;
  font-weight: 400;
`
